<template>
  <ApolloMutation
    :mutation="(gql) => applyCouponMutation"
    :variables="{
      input: {
        id: orderId,
        couponCode: couponCode != '' ? couponCode : null,
      },
    }"
    @done="onDone"
  >
    <template #default="{ mutate, loading}">
      <validation-observer
        ref="observer"
        v-slot="{ invalid }"
      >
        <div class="coupon-field">
          <div class="coupon-field__icon">
            <coupon-icon :size="16" />
          </div>
          <EkitabuTextInput
            id="coupon-code-input"
            v-model="couponCode"
            class="coupon-field__input"
            rules="min:3|max:10|alpha_num"
            maxlength="10"
            :label="$t('checkout.couponCode')"
            label-style="display: none"
            :placeholder="$t('checkout.couponCode')"
            :show-errors="false"
            @input="sanitizeInput"
          />
          <ekitabu-button
            class="coupon-field__btn"
            :class="{ 'coupon-field__btn--loading': loading }"
            text
            :height="44"
            :min-width="0"
            :ripple="false"
            :disabled="loading || invalid || !couponCode || !isNewCode"
            @click="mutate"
          >
            <spinner-icon
              v-if="loading"
              :size="14"
            />
            <template v-else>
              {{ $t("common.apply") }}
            </template>
          </ekitabu-button>
        </div>
      </validation-observer>
    </template>
  </ApolloMutation>
</template>

<script>
import EkitabuTextInput from "@/components/EkitabuTextInput";
import CouponIcon from "@/components/icons/CouponIcon.vue";
import SpinnerIcon from "@/components/icons/SpinnerIcon.vue";
import CouponMutations from "@/graphql/CouponMutations";
import { max, min, alpha_num } from "vee-validate/dist/rules";
import { extend, ValidationObserver, setInteractionMode } from "vee-validate";
import i18n from "@/i18n";

setInteractionMode("eager");

extend("max", {
  ...max,
  message: i18n.t("errors.maxLength", {
    field: "{_field_}",
    length: "{length}",
  }),
});

extend("min", {
  ...min,
  message: i18n.t("errors.minLength", {
    field: "{_field_}",
    length: "{length}",
  }),
});

extend("alpha_num", {
  ...alpha_num,
  message: i18n.t("errors.alphaNum"),
});

export default {
  name: "ApplyCoupon",
  components: {
    EkitabuTextInput,
    ValidationObserver,
    CouponIcon,
    SpinnerIcon,
  },
  props: {
    orderId: {
      type: String,
      required: true,
    },
    existingCouponCode: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      couponCode: this.existingCouponCode,
    };
  },
  computed: {
    applyCouponMutation() {
      return CouponMutations.applyCoupon;
    },
    isNewCode() {
      if (this.existingCouponCode === null) return true;
      return this.existingCouponCode !== this.couponCode;
    },
  },
  watch: {
    existingCouponCode(val) {
      this.couponCode = val;
    },
  },
  methods: {
    sanitizeInput(val) {
      if (!val) return;
      const clean = val.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10);
      if (clean !== val) {
        this.couponCode = clean;
      }
    },
    onDone() {
      this.$emit("cartUpdated");
      this.$emit("snackbarMessage", this.$t('checkout.couponApplied'));
    },
  },
}
</script>

<style scoped lang="scss">
.coupon-field {
  display: flex;
  align-items: center;
  border: 1px solid $book-grey-lighter;
  border-radius: 8px;
  background: white;
  overflow: hidden;
  transition: border-color 0.15s;
  height: 44px;

  &:focus-within {
    border-color: $book-red;
  }
}

.coupon-field__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 12px;
  color: $book-grey;
  flex-shrink: 0;
  line-height: 0;
}

.coupon-field__input {
  flex: 1;
  min-width: 0;
}

/* Strip all Vuetify chrome from the nested text field */
::v-deep .coupon-field__input .input-container {
  margin: 0 !important;
  padding: 0 !important;
}

::v-deep .coupon-field__input .input-field {
  border: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

::v-deep .coupon-field__input .v-input {
  margin: 0 !important;
  padding: 0 !important;
}

::v-deep .coupon-field__input .v-input__control {
  min-height: 0 !important;
}

::v-deep .coupon-field__input .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  min-height: 42px !important;
  height: 42px !important;
  padding: 0 8px !important;
  margin: 0 !important;
  align-items: center !important;
}

::v-deep .coupon-field__input .v-text-field__details {
  display: none !important;
}

::v-deep .coupon-field__input .v-text-field__slot {
  height: 42px !important;
  display: flex !important;
  align-items: center !important;
}

::v-deep .coupon-field__input input {
  font-family: $book-font-body;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $book-ink;
  padding: 0 !important;
  margin: 0 !important;
  height: auto !important;
  max-height: none !important;
  line-height: 42px !important;
  align-self: center !important;

  &::placeholder {
    color: $book-grey !important;
    font-weight: 500 !important;
    opacity: 1 !important;
    line-height: 42px !important;
  }
}

.coupon-field__btn {
  flex-shrink: 0;
  height: 44px;
  padding: 0 18px;
  border: none;
  background: none;
  font-family: $book-font-body;
  font-size: 0.9375rem;
  font-weight: 700;
  color: $book-grey-dark;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  border-left: 1px solid $book-grey-lighter;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: rgba($book-grey-dark, 0.06);
  }

  &:disabled {
    color: $book-grey-light;
    cursor: not-allowed;
  }
}

@media (max-width: 599px) {
  ::v-deep .coupon-field__input input {
    font-size: 1rem !important;
  }
}

</style>
